import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { Pagination } from '@material-ui/lab';
import {
  TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL,
  TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL,
  TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
} from 'config/urls';
import { debounce, get } from 'lodash';
import { useSectionAssignments, useSectionDetail } from 'sdk';
import { cutText } from 'utils';

import AssignmentNavSearch from 'pages/Teachers/AssignmentsList/components/AssignmentNavSearch';
import AssignmentTable from 'pages/Teachers/AssignmentsList/components/AssignmentTable';
import OverallPerformanceCard from 'pages/Teachers/AssignmentsList/components/OverallPerformanceCard';
import NavTitle from 'pages/Teachers/Home/components/NavTitle';
import { useNavbar } from 'pages/Teachers/shared';
import { formatDate } from 'utils/date';
import { buildFilters } from 'utils/filters';
import { useEdLightTracking } from 'utils/integrations/mixpanel';
import { parseParams, reverse, stringifyParams } from 'utils/urls';

import AssignmentDialogContext from 'components/AssignmentDialogContext';

import { ReactComponent as AssignmentIcon } from './assets/assignment.svg';
import styles from './styles.module.scss';

const AssignmentList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [globalQuery, setGlobalQuery] = useState('');
  const [globalFiltersOpen, setGlobalFiltersOpen] = useState(false);

  const [count, setCount] = useState(0);
  const [assignmentsFilter, setAssignmentsFilter] = useState({
    search: '',
    standards: [],
    startDate: '',
    endDate: ''
  });

  const { setIsAssignmentDialogOpened } = useContext(AssignmentDialogContext);
  const { sectionId } = useParams();
  const history = useHistory();

  const searchParams = parseParams(history.location.search);
  const page = get(searchParams, 'currentPage');

  const filters = useMemo(
    () => buildFilters(assignmentsFilter),
    [assignmentsFilter]
  );

  const { data: assignmentsData, isLoading: assignmentsDataIsLoading } =
    useSectionAssignments({
      sectionId,
      params: {
        search: filters.search,
        page: currentPage,
        page_size: 4,
        start_date: filters.startDate
          ? formatDate({
              date: filters.startDate,
              format: 'YYYY-MM-DD'
            })
          : null,
        end_date: filters.endDate
          ? formatDate({
              date: filters.endDate,
              format: 'YYYY-MM-DD'
            })
          : null
      }
    });

  useEffect(() => {
    if (page) {
      setCurrentPage(Number(page));
    }
  }, [page]);

  useEffect(() => {
    if (assignmentsData) {
      setCount(Math.round(assignmentsData.count / 4));
    }
  }, [assignmentsData, setCount]);

  const { trackEvent } = useEdLightTracking({
    feature: 'assignments',
    view: 'list'
  });

  const { data: sectionDetail, isLoading: isSectionDetailLoading } =
    useSectionDetail({
      sectionId: sectionId
    });

  const handlePageChange = (event, value) => {
    setCurrentPage(value);

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('currentPage', value);
    history.push({
      pathname: window.location.pathname,
      search: searchParams.toString()
    });
  };

  const handleRowNavigation = ({ assignmentId }) => {
    trackEvent('Assignment selected', { assignment_id: assignmentId });
    history.push({
      pathname: reverse(
        sectionDetail.is_math
          ? TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
          : TEACHER_ASSIGNMENT_V2_INSIGHTS_URL,
        {
          trackerInstanceSectionId: assignmentId
        }
      ),
      search: stringifyParams({ assignmentsPage: currentPage, sectionId })
    });
  };

  const handleUploadAssignment = () => {
    trackEvent('Upload Student Works Button Clicked');

    history.push({
      pathname: reverse(TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL)
    });
  };

  const handleCreateAssignment = () => {
    trackEvent('Create Assignment Button Clicked');

    setIsAssignmentDialogOpened(true);
  };

  const runSearch = useMemo(
    () =>
      debounce((search) => {
        setCurrentPage(1);
        setAssignmentsFilter((prev) => ({
          ...prev,
          search: search['search'] || ''
        }));
      }, 1000),
    []
  );

  const sendEvent = useMemo(
    () =>
      debounce((search) => {
        if (search['search'] !== '') {
          trackEvent('Assignment Search', { search: search['search'] });
        }
      }, 1000),
    [trackEvent]
  );

  const handleQueryChange = useCallback(
    ({ target }) => {
      setGlobalQuery(target.value);
      runSearch({ search: target.value });
      sendEvent({ search: target.value });
    },
    [runSearch, sendEvent]
  );

  const handleToggleFilters = useCallback(() => {
    setGlobalFiltersOpen((prev) => !prev);
  }, []);

  const handleStandardChange = useCallback(
    ({ target }) => {
      trackEvent('Assignment Filtered by Standard', {
        standards: target.value
      });
      setAssignmentsFilter((prev) => ({
        ...prev,
        standards: target.value
      }));
      setCurrentPage(1);
    },
    [trackEvent]
  );

  const handleStartDateChange = useCallback(
    (date) => {
      trackEvent('Assignment Filtered by Start Date', {
        startDate: formatDate({
          date,
          format: 'YYYY-MM-DD'
        })
      });
      setAssignmentsFilter((prev) => ({
        ...prev,
        startDate: date
      }));
      setCurrentPage(1);
    },
    [trackEvent]
  );

  const handleEndDateChange = useCallback(
    (date) => {
      trackEvent('Assignment Filtered by End Date', {
        endDate: formatDate({
          date,
          format: 'YYYY-MM-DD'
        })
      });
      setAssignmentsFilter((prev) => ({
        ...prev,
        endDate: date
      }));
      setCurrentPage(1);
    },
    [trackEvent]
  );

  const handleResetFilters = useCallback(() => {
    trackEvent('Assignment Filters Reset');
    setAssignmentsFilter({
      search: '',
      standards: [],
      startDate: '',
      endDate: ''
    });
    setGlobalFiltersOpen(false);
    setGlobalQuery('');
  }, [trackEvent]);

  const navConfig = useMemo(
    () => ({
      title: (
        <NavTitle
          title="Assignments List"
          subtitle={cutText({
            text: sectionDetail?.name,
            char: 40
          })}
          Icon={AssignmentIcon}
        />
      ),
      RightSubheader: (
        <AssignmentNavSearch
          query={globalQuery}
          sectionId={sectionId}
          onQueryChange={handleQueryChange}
          filtersActive={globalFiltersOpen}
          onToggleFilters={handleToggleFilters}
          standardValue={assignmentsFilter.standards}
          onStandardChange={handleStandardChange}
          startDate={assignmentsFilter.startDate}
          onStartDateChange={handleStartDateChange}
          endDate={assignmentsFilter.endDate}
          onEndDateChange={handleEndDateChange}
          onResetFilters={handleResetFilters}
        />
      ),

      isTransparent: true
    }),
    [
      sectionDetail?.name,
      sectionId,
      globalFiltersOpen,
      assignmentsFilter.standards,
      assignmentsFilter.startDate,
      assignmentsFilter.endDate,
      handleQueryChange,
      handleToggleFilters,
      handleStandardChange,
      handleStartDateChange,
      handleEndDateChange,
      handleResetFilters,
      globalQuery
    ]
  );

  useNavbar(navConfig);

  const showMathContent = !isSectionDetailLoading && sectionDetail?.is_math;

  return (
    <div className={styles.pageContainer}>
      <main className={styles.workspaceWrapper}>
        <OverallPerformanceCard
          show={showMathContent}
          onUploadAssignment={handleUploadAssignment}
          onAddAssignment={handleCreateAssignment}
          intervene={assignmentsData?.total_intervene_percentage}
          support={assignmentsData?.total_support_percentage}
          celebrate={assignmentsData?.total_celebrate_percentage}
          dataIsLoading={assignmentsDataIsLoading}
        />

        <AssignmentTable
          isMath={showMathContent}
          onRowNavigation={handleRowNavigation}
          data={assignmentsData?.results}
          dataIsLoading={assignmentsDataIsLoading}
        />

        {count !== 0 && (
          <div className={styles.paginationWrapper}>
            <Pagination
              count={count}
              page={currentPage}
              onChange={handlePageChange}
              variant="text"
              shape="rounded"
            />
          </div>
        )}
      </main>
    </div>
  );
};

export default AssignmentList;
